import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ServiceAppointmentService } from '../../../../core/data-services/service-appointment.service';
import { OrganizationService } from '../../../../core/data-services/organization.service';
import { DroneService } from '../../../../core/data-services/drone.service';
import { Drone } from '../../../../core/data-models/drone.model';
import { ApiError } from '../../../../abstract/api-error';
import { FileUploadService } from '../../../../core/api-services/file-upload.service';
import { UploadedFileInfo } from '../../../../core/response-models/uploaded-file-info.model';
import { ToastService } from '../../../../core/services/toast.service';
import { PREFERRED_TIME_OPTIONS } from '../../../../shared/constants/service-appointment-options';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

/**
 * File selected for upload (before actual upload)
 */
interface SelectedFile {
  file: File;
  name: string;
  size: number;
  type: string;
  isUploading: boolean;
  uploadProgress: number;
  uploadedInfo?: UploadedFileInfo;
  error?: string;
}

@Component({
  selector: 'app-service-appointment-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './service-appointment-form.component.html',
  styleUrls: ['./service-appointment-form.component.scss']
})
export class ServiceAppointmentFormComponent implements OnInit {
  form!: FormGroup;
  isLoading = false;
  isSaving = false;
  organizationId: string | null = null;
  drones: Drone[] = [];
  private droneService = inject(DroneService);
  isDronesLoading = false;

  preferredTimeOptions = PREFERRED_TIME_OPTIONS;
  minDate: string;

  // File upload state
  selectedFiles: SelectedFile[] = [];
  maxFiles = 5;
  maxFileSize: number;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private appointmentService: ServiceAppointmentService,
    private organizationService: OrganizationService,
    private fileUploadService: FileUploadService,
    private toastService: ToastService
  ) {
    // Set min date to today
    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];
    this.maxFileSize = this.fileUploadService.getMaxFileSize();
  }

  ngOnInit(): void {
    this.initForm();
    this.initialize();
  }

  private initForm(): void {
    this.form = this.fb.group({
      droneId: ['', Validators.required],
      preferredDate: ['', Validators.required],
      preferredTime: ['', Validators.required],
      issueDescription: ['', [Validators.required, Validators.minLength(20)]]
    });
  }

  private async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      const org = await this.organizationService.getMyOrganization();
      if (!org) {
        this.toastService.error('No organization found');
        this.goBack();
        return;
      }
      this.organizationId = org.id;
      await this.loadDrones();
    } catch (error) {
      console.error('Error initializing form:', error);
      this.toastService.error('Failed to load form data');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadDrones(): Promise<void> {
    this.isDronesLoading = true;
    try {
      this.drones = (await this.droneService.getOrganizationDrones()).filter(drone => drone.ownership === 'adn');
    } catch (error) {
      console.error('Error loading drones:', error);
      this.drones = [];
    } finally {
      this.isDronesLoading = false;
    }
  }

  /**
   * Handle file selection from input
   */
  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);

    for (const file of files) {
      // Check if we've reached max files
      if (this.selectedFiles.length >= this.maxFiles) {
        this.toastService.warning(`Maximum ${this.maxFiles} files allowed`);
        break;
      }

      // Check file type
      if (!this.fileUploadService.isAllowedFileType(file)) {
        this.toastService.error(`File type not allowed: ${file.name}`);
        continue;
      }

      // Check file size
      if (file.size > this.maxFileSize) {
        this.toastService.error(`File too large: ${file.name} (max ${this.formatFileSize(this.maxFileSize)})`);
        continue;
      }

      // Add to selected files
      this.selectedFiles.push({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        isUploading: false,
        uploadProgress: 0,
      });
    }

    // Reset input
    input.value = '';
  }

  /**
   * Remove a selected file
   */
  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    return this.fileUploadService.formatFileSize(bytes);
  }

  /**
   * Get file icon based on type
   */
  getFileIcon(type: string): string {
    if (type.startsWith('image/')) return 'bi-file-image';
    if (type === 'application/pdf') return 'bi-file-pdf';
    if (type.includes('word')) return 'bi-file-word';
    return 'bi-file-earmark';
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.organizationId) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    try {
      // Upload files first if any
      const uploadedFiles: UploadedFileInfo[] = [];
      if (this.selectedFiles.length > 0) {
        for (const selectedFile of this.selectedFiles) {
          if (selectedFile.uploadedInfo) {
            // Already uploaded
            uploadedFiles.push(selectedFile.uploadedInfo);
            continue;
          }

          selectedFile.isUploading = true;
          try {
            const info = await this.fileUploadService.uploadServiceAppointmentFile(selectedFile.file);
            selectedFile.uploadedInfo = info;
            selectedFile.uploadProgress = 100;
            uploadedFiles.push(info);
          } catch (error: unknown) {
            selectedFile.error = error instanceof Error && error.message ? error.message : 'Upload failed';
            console.error(`Failed to upload ${selectedFile.name}:`, error);
          } finally {
            selectedFile.isUploading = false;
          }
        }
      }

      // Create the appointment with attachments
      await this.appointmentService.createAppointment({
        organizationId: this.organizationId,
        droneId: this.form.value.droneId,
        preferredDate: this.form.value.preferredDate,
        preferredTime: this.form.value.preferredTime,
        issueDescription: this.form.value.issueDescription,
        attachments: uploadedFiles.map(f => ({
          fileName: f.fileName,
          fileSize: f.fileSize,
          fileType: f.fileType,
          fileExtension: f.fileExtension,
          s3Key: f.s3Key,
          s3Url: f.s3Url,
        }))
      });

      this.toastService.success('Service appointment requested successfully');
      this.goBack();
    } catch (error: unknown) {
      this.toastService.error(ApiError.fromHttpError(error).errors[0] ?? 'Failed to create appointment. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'This field is required';
    if (field.errors['minlength']) {
      return `Must be at least ${field.errors['minlength'].requiredLength} characters`;
    }
    return 'Invalid value';
  }

  goBack(): void {
    this.router.navigate(['/maintenance/service-appointments']);
  }
}
